import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Flag, AlertTriangle, Ban, MessageSquareOff, Shield } from 'lucide-react';

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam ou publicité', icon: <MessageSquareOff className="w-4 h-4" /> },
  { value: 'harassment', label: 'Harcèlement ou intimidation', icon: <Ban className="w-4 h-4" /> },
  { value: 'inappropriate', label: 'Contenu inapproprié', icon: <AlertTriangle className="w-4 h-4" /> },
  { value: 'misinformation', label: 'Désinformation', icon: <Shield className="w-4 h-4" /> },
  { value: 'other', label: 'Autre raison', icon: <Flag className="w-4 h-4" /> }
];

const ReportMessage = ({ messageId, messageContent, authorName, onReport }) => {
  const { api, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Veuillez sélectionner une raison');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reports', {
        message_id: messageId,
        reason,
        details,
        message_preview: messageContent?.substring(0, 200)
      });
      toast.success('Signalement envoyé aux modérateurs');
      setOpen(false);
      setReason('');
      setDetails('');
      if (onReport) onReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du signalement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-muted-foreground hover:text-red-500 h-7 px-2"
          data-testid={`report-btn-${messageId}`}
        >
          <Flag className="w-3.5 h-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-red-500">
            <Flag className="w-5 h-5" />
            Signaler un message
          </DialogTitle>
          <DialogDescription>
            Signaler un message de <span className="font-semibold">{authorName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Message Preview */}
          <div className="p-3 bg-secondary/50 rounded-lg border border-border">
            <p className="text-sm text-muted-foreground line-clamp-3">
              "{messageContent?.substring(0, 150)}{messageContent?.length > 150 ? '...' : ''}"
            </p>
          </div>

          {/* Reason Selection */}
          <div className="space-y-2">
            <Label>Raison du signalement *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="bg-secondary/50">
                <SelectValue placeholder="Sélectionnez une raison" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    <div className="flex items-center gap-2">
                      {r.icon}
                      {r.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Additional Details */}
          <div className="space-y-2">
            <Label>Détails supplémentaires (optionnel)</Label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Expliquez pourquoi ce message pose problème..."
              className="bg-secondary/50"
              rows={3}
            />
          </div>

          <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-xs text-yellow-500 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              Les signalements abusifs peuvent entraîner des sanctions. Utilisez cette fonctionnalité de manière responsable.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !reason}
            className="bg-red-500 text-white hover:bg-red-600"
          >
            <Flag className="w-4 h-4 mr-2" />
            {submitting ? 'Envoi...' : 'Envoyer le signalement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportMessage;
